import React, { useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { 
  ThemeProvider, 
  createTheme, 
  CssBaseline, 
  Box,
  CircularProgress,
} from '@mui/material';
import Sidebar from './components/internal_layout/Sidebar';
import ManagePreferences from './components/internal_layout/ManagePreferences';
import InsightSidebar from './components/internal_layout/InsightSidebar';
import { InsightProvider } from './contexts/InsightContext';
import Footer from './components/internal_layout/Footer';
import Home from './components/home/Home';
import DistrictAnalysis from './components/district_analysis/DistrictAnalysis';
import Login from './components/external_site/Login';
import ResetPassword from './components/external_site/ResetPassword';
import ProtectedRoute from './components/ProtectedRoute';
import ProductProtectedRoute from './components/ProductProtectedRoute';
import CityExplorerHome from './components/city_explorer/CityExplorerHome';
import LocationExplorer from './components/city_explorer/LocationExplorer';
import SharedLocationExplorer from './components/city_explorer/SharedLocationExplorer';
import AISummaryRouteGuard from './components/AISummaryRouteGuard';
import SchoolSummary from './components/school_summary/SchoolSummary';
import GeographicExplorer from './components/geographic_explorer/GeographicExplorer';
import CompetitiveExplorer from './components/competitive_explorer/CompetitiveExplorer';
import ProjectionsExplorer from './components/projections_explorer/ProjectionsExplorer';
import TermsOfService from './components/external_site/TermsOfService'; 
import PrivacyNotice from './components/external_site/PrivacyNotice';
import HomePage from './components/external_site/HomePage';
import AboutUs from './components/external_site/AboutUs';
import Contact from './components/external_site/Contact';
import ProductPage from './components/external_site/ProductPage';
import PublicCampusSummary from './components/external_site/PublicCampusSummary';
import SelectTool from './components/tool_picker/SelectTool';

import UserManagement from './components/admin/user_management/UserManagement';
import TeamManagement from './components/admin/user_management/TeamManagement';
import PublicLinksManagement from './components/admin/public_links/PublicLinksManagement';
import TeamGroups from './components/admin/user_management/TeamGroups';
import SchoolsValidation from './components/admin/SchoolsValidation';
import SchoolProcessorV2 from './components/admin/school_processor_v2/SchoolProcessorV2';
import AdminAnalytics from './components/admin/AdminAnalytics';
import AcademicData from './components/admin/AcademicData';
import AdminFeedback from './components/admin/AdminFeedback';
import AdminDebugTools from './components/admin/AdminDebugTools';
import TxDistrictExplorer from './components/admin/TxDistrictExplorer';
// AICompetitiveAnalysis removed - functionality merged into AI Summary tool
import AISummary from './components/admin/ai_summary/AISummary';
import { UserAISummary } from './components/ai_summary';
import DropAPinDashboard from './components/admin/DropAPinDashboard';
import StudentCatchmentTool from './components/student_catchment_explorer/StudentCatchmentTool';
import ProjectionsAnalysis from './components/admin/projections_analysis/ProjectionsAnalysis';
import IconExporter from './components/admin/IconExporter';
import LoginAnalytics from './components/admin/LoginAnalytics';
import ProductUsage from './components/admin/ProductUsage';
import PrivacyConsentManager from './components/external_site/PrivacyConsentManager';
import AnnouncementDialog from './components/shared_components/AnnouncementDialog';
import Resources from './components/resources/Resources';
import Support from './components/support/Support';
import VideoTutorials from './components/resources/VideoTutorials';
import MethodologyDocs from './components/resources/MethodologyDocs';

import { AuthProvider, useAuth } from './auth/AuthContext';
import { AISummaryProvider } from './contexts/AISummaryContext';
import { SummaryCacheProvider } from './hooks/useSummaryCache';
import useSchoolStore from './stores/schoolStore';
import { getAnalytics, setUserId, logEvent } from 'firebase/analytics';
import { SnackbarProvider } from 'notistack';
import PasswordSetup from './components/external_site/PasswordSetup';
import AccountActivation from './components/external_site/AccountActivation';
import ChangePassword from './components/internal_layout/ChangePassword';
import { SessionWarningDialog } from './auth/components/SessionWarningDialog';
import { usePrivacyConsent } from './hooks/usePrivacyConsent';
import { useVersionHandler } from './hooks/useVersionHandler';
import config from './config';
import { initializeGA } from './utils/initializeGA';
import { initializeAnalytics } from './utils/analytics';


const theme = createTheme({
  palette: {
    primary: {
      main: '#4338ca',
      light: '#6366f1',
    },
    background: {
      default: '#ffffff',
      paper: '#ffffff',
    },
    grey: {
      200: '#f3f4f6',
    },
  },
});

// The drop-a-pin projections tool now lives in the Projections Explorer; keep old links working.
function RedirectToProjectionsExplorer() {
  const { search } = useLocation();
  return <Navigate to={`/projections-explorer${search}`} replace />;
}

function AppContent() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const { analyticsAllowed, initialized, showBanner } = usePrivacyConsent();
  
  // Add this line to use the version check hook
  useVersionHandler();

  // Initialize Google Analytics with environment-specific ID
  useEffect(() => {
    if (initialized && analyticsAllowed && config.gaMeasurementId) {
      initializeGA(config.gaMeasurementId);
    }
  }, [initialized, analyticsAllowed]);

  // Initialize analytics with user context when user logs in
  useEffect(() => {
    if (user && initialized && analyticsAllowed) {
      initializeAnalytics(user);
    }
  }, [user, initialized, analyticsAllowed]);

  const shouldHideInsightSidebar = () => {
    const hiddenPaths = ['/dashboard', '/ai-summary', '/admin', '/admin/user-management', '/admin/team-management', '/admin/team-groups-management', '/schools-validation', '/preferences', '/resources', '/resources/methodology', '/resources/tutorials', '/admin/tx-district-tool', '/admin/projections-analysis', '/admin/login-analytics', '/admin/csgf-reporting', '/admin/product-usage'];
    // InsightSidebar is a School Explorer-specific panel; City Explorer has its own shell.
    if (location.pathname.startsWith('/city-explorer')) return true;
    return hiddenPaths.includes(location.pathname);
  };
  
  // Simplified analytics tracking
  useEffect(() => {
    if (!user || !initialized || !analyticsAllowed) return;
    
    const analytics = getAnalytics();
    setUserId(analytics, user.uid);
    const sessionStartTime = Date.now();
    
    logEvent(analytics, 'session_start', {
      user_id: user.uid,
      email: user.email,
    });

    return () => {
      logEvent(analytics, 'session_end', {
        user_id: user.uid,
        session_duration: Date.now() - sessionStartTime
      });
    };
  }, [user, initialized, analyticsAllowed]);

  // Simplified store initialization
  useEffect(() => {
    if (!loading && user) {
      useSchoolStore.getState().fetchSchools().catch(error => {
        console.error('Error fetching schools:', error);
        useSchoolStore.getState().clearAllData();
      });
    } else if (!loading && !user) {
      useSchoolStore.getState().clearAllData();
    }
  }, [loading, user]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const handleSidebarToggle = () => {};

  const publicPages = ['/', '/about', '/login', '/reset-password', '/terms-of-service', '/privacy-notice', '/product', '/contact', '/set-password', '/activate'];
  // Pages that hide the sidebar/footer chrome even though they require auth -
  // the tool picker renders before we know which product's shell to show.
  const noChromePages = ['/select-tool'];
  // Check if current path is a public page or a public campus page
  const isPublicPage = publicPages.includes(location.pathname)
    || noChromePages.includes(location.pathname)
    || location.pathname.startsWith('/campuses/')
    || location.pathname.startsWith('/share/');
  const hideInsightSidebar = shouldHideInsightSidebar();

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Box 
        component="main" 
        sx={{ 
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          pl: isPublicPage ? 0 : '70px',
          // Only add right padding if insight sidebar should be shown
          pr: (isPublicPage || hideInsightSidebar) ? 0 : '60px',
          boxSizing: 'border-box',
          position: 'relative',
        }}
      >
        {/* Routes remain the same */}
        <Box sx={{ 
          flexGrow: 1,
          width: '100%',
          boxSizing: 'border-box',
          overflow: 'visible',
          position: 'relative',
        }}>
          {/* Public routes */}
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/set-password" element={<PasswordSetup />} />
            <Route path="/activate" element={<AccountActivation />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/privacy-notice" element={<PrivacyNotice />} />
            <Route path="/product" element={<ProductPage />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/campuses/:schoolSlug" element={<PublicCampusSummary />} />
            <Route path="/share/:shareId" element={<SharedLocationExplorer />} />
            <Route path="/share/:shareId/documents/:resourceId" element={<SharedLocationExplorer />} />
            <Route path="/share/:shareId/:categoryId/:fileId" element={<SharedLocationExplorer />} />

            {/* Protected routes */}
            <Route path="/select-tool"
              element={<ProtectedRoute><SelectTool /></ProtectedRoute>}
            />

            {/* City Explorer - separate product, gated by product entitlement rather than role */}
            <Route path="/city-explorer"
              element={<ProductProtectedRoute requiredProduct="city_explorer"><CityExplorerHome /></ProductProtectedRoute>}
            />
            <Route
              path="/city-explorer/connecticut/opportunity-calculator"
              element={<Navigate to="/city-explorer/connecticut/documents/opportunity_calculator" replace />}
            />
            {/* All three paths render the same explorer; the longer forms just deep-link
                a specific map or document. React Router ranks the static "documents"
                segment above :categoryId, so map links are unaffected. */}
            <Route path="/city-explorer/:locationId"
              element={<ProductProtectedRoute requiredProduct="city_explorer"><LocationExplorer /></ProductProtectedRoute>}
            />
            <Route path="/city-explorer/:locationId/documents/:resourceId"
              element={<ProductProtectedRoute requiredProduct="city_explorer"><LocationExplorer /></ProductProtectedRoute>}
            />
            <Route path="/city-explorer/:locationId/:categoryId/:fileId"
              element={<ProductProtectedRoute requiredProduct="city_explorer"><LocationExplorer /></ProductProtectedRoute>}
            />

            <Route path="/dashboard" 
              element={<ProtectedRoute><Home user={user} /></ProtectedRoute>} 
            />

            <Route path="/district-analysis" 
              element={<ProtectedRoute><DistrictAnalysis /></ProtectedRoute>} 
            />

            <Route path="/school-summary" 
              element={<ProtectedRoute><SchoolSummary /></ProtectedRoute>} 
            />

            <Route path="/school-summary/:schoolId" 
              element={<ProtectedRoute><SchoolSummary /></ProtectedRoute>} 
            />

            <Route path="/projections-explorer" 
              element={
                <ProtectedRoute allowedRoles={['admin', 'updated_write', 'updated_read', 'public']}>
                  <ProjectionsExplorer />
                </ProtectedRoute>
              } 
            />

            <Route path="/student-catchment-explorer"
              element={
                <ProtectedRoute allowedRoles={['admin', 'updated_write', 'updated_read', 'public']}>
                  <StudentCatchmentTool />
                </ProtectedRoute>
              }
            />

            <Route path="/geographic-explorer" 
              element={
                <ProtectedRoute allowedRoles={['admin', 'updated_write', 'updated_read', 'public']}>
                  <GeographicExplorer />
                </ProtectedRoute>
              } 
            />

            <Route path="/competitive-explorer" 
              element={
                <ProtectedRoute allowedRoles={['admin', 'updated_write', 'updated_read', 'public']}>
                  <CompetitiveExplorer />
                </ProtectedRoute>
              } 
            />

            <Route path="/ai-summary" 
              element={
                <ProtectedRoute allowedRoles={['admin', 'updated_write', 'updated_read', 'public']}>
                  <AISummaryRouteGuard>
                    <UserAISummary />
                  </AISummaryRouteGuard>
                </ProtectedRoute>
              } 
            />

            <Route path="/admin" 
              element={<Navigate to="/admin/user-management" replace />} 
            />

            <Route path="/admin/user-management" 
              element={<ProtectedRoute allowedRoles={['admin']}><UserManagement /></ProtectedRoute>} 
            />

            <Route path="/admin/team-management" 
              element={<ProtectedRoute allowedRoles={['admin']}><TeamManagement /></ProtectedRoute>} 
            />

            <Route path="/public-links" 
              element={<ProtectedRoute allowedRoles={['admin']}><PublicLinksManagement /></ProtectedRoute>} 
            />

            <Route path="/admin/team-groups-management" 
              element={<ProtectedRoute allowedRoles={['admin']}><TeamGroups /></ProtectedRoute>} 
            />

            <Route path="/schools-validation" 
              element={<ProtectedRoute allowedRoles={['admin']}><SchoolsValidation /></ProtectedRoute>} 
            />

            <Route path="/admin/school-processor/*" 
              element={<ProtectedRoute allowedRoles={['admin']}><SchoolProcessorV2 /></ProtectedRoute>} 
            />

            <Route path="/admin/analytics" 
              element={<ProtectedRoute allowedRoles={['admin']}><AdminAnalytics /></ProtectedRoute>} 
            />

            <Route path="/admin/academic-data" 
              element={<ProtectedRoute allowedRoles={['admin']}><AcademicData /></ProtectedRoute>} 
            />

            <Route path="/admin/feedback" 
              element={<ProtectedRoute allowedRoles={['admin']}><AdminFeedback /></ProtectedRoute>} 
            />

            <Route path="/admin/debug" 
              element={<ProtectedRoute allowedRoles={['admin']}><AdminDebugTools /></ProtectedRoute>} 
            />

            <Route path="/admin/tx-district-tool" 
              element={<ProtectedRoute allowedRoles={['admin']}><TxDistrictExplorer /></ProtectedRoute>} 
            />

            <Route path="/admin/ai-summary" 
              element={<ProtectedRoute allowedRoles={['admin']}><AISummary /></ProtectedRoute>} 
            />

            <Route path="/admin/drop-a-pin" 
              element={<ProtectedRoute allowedRoles={['admin']}><DropAPinDashboard /></ProtectedRoute>} 
            />

            <Route path="/admin/drop-a-pin-projections"
              element={<RedirectToProjectionsExplorer />}
            />

            <Route path="/admin/student-catchment"
              element={<Navigate to="/student-catchment-explorer" replace />}
            />

            <Route path="/admin/projections-analysis" 
              element={<ProtectedRoute allowedRoles={['admin']}><ProjectionsAnalysis /></ProtectedRoute>} 
            />

            <Route path="/admin/icon-exporter" 
              element={<ProtectedRoute allowedRoles={['admin']}><IconExporter /></ProtectedRoute>} 
            />

            <Route path="/admin/login-analytics" 
              element={<ProtectedRoute allowedRoles={['admin']}><LoginAnalytics /></ProtectedRoute>} 
            />

            <Route path="/admin/csgf-reporting"
              element={<Navigate to="/admin/product-usage?tab=csgf" replace />}
            />

            <Route path="/admin/product-usage"
              element={<ProtectedRoute allowedRoles={['admin']}><ProductUsage /></ProtectedRoute>}
            />

            <Route path="/preferences" 
              element={<ProtectedRoute><ManagePreferences /></ProtectedRoute>} 
            />

            <Route path="/change-password"
              element={<ProtectedRoute><ChangePassword /></ProtectedRoute>}
            />

            <Route path="/support/*" 
              element={<ProtectedRoute><Support /></ProtectedRoute>} 
            />

            <Route path="/resources/*" 
              element={<ProtectedRoute><Resources /></ProtectedRoute>} 
            />

            <Route path="/resources/methodology"
              element={<ProtectedRoute><MethodologyDocs /></ProtectedRoute>}
            />

            <Route path="/support/tutorials"
              element={<ProtectedRoute><VideoTutorials /></ProtectedRoute>}
            />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Box>
        {!isPublicPage && <Footer />}
      </Box>

      {!isPublicPage && (
        <>
          {/* Navigation Sidebar - fixed position */}
          <Box sx={{ position: 'fixed', left: 0, top: 0, height: '100vh', zIndex: 1200 }}>
            <Sidebar 
              user={user}
              onToggle={handleSidebarToggle}
            />
          </Box>

          {/* Insight Sidebar - only show if not in hidden paths */}
          {!hideInsightSidebar && (
            <Box sx={{ position: 'fixed', right: 0, top: 0, height: '100vh', zIndex: 1200 }}>
              <InsightSidebar />
            </Box>
          )}

          {/* One-time announcement - deferred until the cookie banner is resolved */}
          {user && !showBanner && <AnnouncementDialog />}
        </>
      )}
    </Box>
  );
}

function AppWithRouter() {
  const navigate = useNavigate();
  
  const handleLogoutNavigation = () => {
    navigate('/login');
  };

  return (
    <AuthProvider onLogout={handleLogoutNavigation}>
      <AISummaryProvider>
        <InsightProvider>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <SnackbarProvider
              maxSnack={3}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              autoHideDuration={5000}
            >
              <SummaryCacheProvider>
                <PrivacyConsentManager />
                <SessionWarningDialog /> 
                <AppContent />
              </SummaryCacheProvider>
            </SnackbarProvider>
          </ThemeProvider>
        </InsightProvider>
      </AISummaryProvider>
    </AuthProvider>
  );
}

function App() {
  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <AppWithRouter />
    </Router>
  );
}

export default App;